import { useEffect, useState } from 'react';
import { IconCalendarEvent, IconClockExclamation } from '@tabler/icons-react';
import { ApiError, api } from '../lib/api';

/**
 * Grille des délais légaux dérivés (Sprint 2 §Délais légaux dérivés).
 * Fetch /dossiers/<id>/timeline-v2/deadlines et affiche en 2 colonnes :
 * - Gauche : prochaines échéances actives (61ter, CC, …)
 * - Droite : délais post-décision (appel, opposition, pourvoi)
 */

type Status = 'ok' | 'soon' | 'urgent' | 'overdue' | null;

type Deadlines = {
  next_61ter_eligibility: string | null;
  next_61ter_status: Status;
  appeal_deadline: string | null;
  appeal_status: Status;
  pourvoi_deadline: string | null;
  pourvoi_status: Status;
  opposition_deadline: string | null;
  opposition_status: Status;
  next_cc: string | null;
  next_cc_status: Status;
  detention_active: boolean;
  actes_count: number;
};

type Props = {
  dossierId: string;
};

const STATUS_BADGE: Record<NonNullable<Status>, string> = {
  ok: 'bg-cat-fav-pastel text-cat-fav',
  soon: 'bg-cat-imp-pastel text-cat-imp-text',
  urgent: 'bg-cat-def-pastel text-cat-def',
  overdue: 'bg-cat-def text-paper-elevated',
};

const STATUS_LABEL: Record<NonNullable<Status>, string> = {
  ok: 'OK',
  soon: 'Bientôt',
  urgent: 'Urgent',
  overdue: 'Échu',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-BE');
}

function daysFromNow(iso: string | null): string {
  if (!iso) return '';
  const target = new Date(iso);
  const now = new Date();
  const diff = Math.round((target.getTime() - now.getTime()) / 86400000);
  if (diff === 0) return "(aujourd'hui)";
  if (diff > 0) return `(J-${diff})`;
  return `(J+${Math.abs(diff)})`;
}

function Row({ label, date, status }: { label: string; date: string | null; status: Status }) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-md border border-rule bg-paper-elevated px-3 py-2 text-[12.5px]">
      <div className="min-w-0">
        <p className="font-medium text-text-primary">{label}</p>
        <p className="mt-0.5 text-[11.5px] text-text-tertiary">
          {fmtDate(date)} {daysFromNow(date)}
        </p>
      </div>
      {status && (
        <span
          className={`shrink-0 rounded-pill px-2 py-[1px] font-mono text-[10px] ${STATUS_BADGE[status]}`}
        >
          {STATUS_LABEL[status]}
        </span>
      )}
    </div>
  );
}

export default function DeadlinesGrid({ dossierId }: Props) {
  const [data, setData] = useState<Deadlines | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<Deadlines>(`/dossiers/${dossierId}/timeline-v2/deadlines`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
      });
    return () => {
      cancelled = true;
    };
  }, [dossierId]);

  if (error) {
    return (
      <div className="rounded-md border border-cat-def/40 bg-cat-def-pastel p-2 text-[12px] text-cat-def">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-md border border-rule bg-paper-elevated p-3 text-[12px] text-text-tertiary">
        Calcul des délais…
      </div>
    );
  }

  // Pas d'échéance active = tout null. On rend une carte « rien à signaler ».
  const hasAny =
    data.next_61ter_eligibility ||
    data.appeal_deadline ||
    data.pourvoi_deadline ||
    data.opposition_deadline ||
    data.next_cc;
  if (!hasAny) {
    return (
      <div className="rounded-md border border-rule bg-paper-card p-3 text-[12px] text-text-tertiary">
        Aucun délai légal dérivé à ce stade (pas de jugement, pas de mandat d'arrêt, pas d'acte
        61ter enregistré).
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <IconCalendarEvent size={16} stroke={1.6} className="text-text-secondary" />
        <h3 className="text-[13px] font-medium text-text-primary">Délais dérivés</h3>
      </div>
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        <div className="space-y-1.5">
          {data.detention_active && data.next_cc && (
            <Row
              label="Prochaine chambre du conseil"
              date={data.next_cc}
              status={data.next_cc_status}
            />
          )}
          {data.next_61ter_eligibility && (
            <Row
              label="Prochaine demande 61ter éligible"
              date={data.next_61ter_eligibility}
              status={data.next_61ter_status}
            />
          )}
        </div>
        <div className="space-y-1.5">
          {data.appeal_deadline && (
            <Row label="Délai d'appel" date={data.appeal_deadline} status={data.appeal_status} />
          )}
          {data.opposition_deadline && (
            <Row
              label="Délai d'opposition"
              date={data.opposition_deadline}
              status={data.opposition_status}
            />
          )}
          {data.pourvoi_deadline && (
            <Row
              label="Délai de pourvoi cassation"
              date={data.pourvoi_deadline}
              status={data.pourvoi_status}
            />
          )}
        </div>
      </div>
      {data.actes_count > 0 && (
        <p className="flex items-center gap-1 text-[11px] text-text-tertiary">
          <IconClockExclamation size={12} stroke={1.6} />
          {data.actes_count} acte(s) procédural(aux) enregistré(s)
        </p>
      )}
    </div>
  );
}
